import React from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { GStatTile } from '@/components/growth/growth-ui';
import { GuideLink } from '@/components/ui/guide-link';
import { KB_BASE } from '@/lib/guides';
import { TRACKING_DOMAIN_SETUP } from '@/components/install-code';
import { OldTagPages, useOldTagCount } from '@/components/settings/old-tag-pages';
import { useFirstPartyStatus, useFirstPartyCost } from '@/hooks/use-tracking-domains';
import { fmtZoned } from '@/lib/utils';

/**
 * IS THIS WORKSPACE'S TRACKING FIRST-PARTY - answered on every screen.
 *
 * Tracking accuracy is the product, and a workspace loading everything from
 * our domain loses visitors to blockers and has Safari forget them after 7
 * days - silently, in every number it reads. So the answer sits in the header
 * beside the data freshness chip, and like that chip it earns attention by
 * CHANGING: nothing at all while the tracking domain works (a quiet count only
 * if pages still load the old tag), amber while the workspace is not
 * first-party, rose when a domain that worked has stopped.
 *
 * The verdict is the server's (api services/firstPartyState), the same one the
 * snippets, the setup checklist and the platform console read. The panel
 * states what it costs in the workspace's OWN data, measured: never an
 * industry average, and every share carries the denominator that could hold
 * it.
 */

const fmt = (n) => (n == null ? '-' : Number(n).toLocaleString('en-IN'));
const day = (ts) => (ts ? fmtZoned(ts, 'd MMM') : null);

function CostTiles() {
  const { data, isLoading, error } = useFirstPartyCost(true);
  if (isLoading) return <p className="text-[13px] text-muted-foreground">Measuring your last 28 days...</p>;
  if (error || !data) {
    return <p className="text-[13px] text-rose-600 dark:text-rose-400">Could not measure this: {error?.response?.data?.error || error?.message || 'no answer'}.</p>;
  }
  const w = data.webkit || {};
  const r = data.returning || {};
  return (
    <div className="space-y-2.5">
      <div className="grid gap-3 sm:grid-cols-2">
        <GStatTile
          label="Visitors on Safari or an iPhone"
          value={w.pct == null ? 'None seen' : `${w.pct}%`}
          caption={w.with_browser
            ? `${fmt(w.webkit)} of ${fmt(w.with_browser)} visitors with a known browser, last 28 days.`
            : 'No visitor with a known browser in the last 28 days.'}
          tip="Every browser on an iPhone or iPad is WebKit, in-app browsers included, and so is Safari on a Mac. WebKit caps a cookie a script writes at 7 days, so on our domain these visitors come back as strangers after a week." />
        <GStatTile
          label="Came back under a new id"
          value={r.identified ? fmt(r.known_before) : 'None yet'}
          caption={r.identified
            ? `of ${fmt(r.identified)} visitors who gave an email or phone; ${fmt(r.same_kind)} on the same kind of device and browser as before.`
            : 'Nobody gave an email or phone in 28 days, so it cannot be judged.'}
          tip="Visitors whose email or phone was already linked to an earlier visitor id. On the same kind of device and browser, the earlier id was most likely lost. On a different device it is a person switching devices, which no tracking setup can join." />
      </div>
      {/* The gaps, said: what could not be counted and why. */}
      <ul className="space-y-1 text-[13px] leading-relaxed text-muted-foreground">
        {w.no_browser > 0 && <li>{fmt(w.no_browser)} visitor{w.no_browser === 1 ? '' : 's'} had no browser recorded, so they are left out of the Safari share rather than counted as either.</li>}
        {r.known_before > 0 && (
          <li>
            Of the {fmt(r.known_before)}: {fmt(r.same_kind)} came back on the same kind of device and browser
            {r.same_kind_webkit > 0 ? <> ({fmt(r.same_kind_webkit)} of them on Safari or an iPhone)</> : null}, {fmt(r.other_device)} on a different one
            {r.not_judged > 0 ? <>, and {fmt(r.not_judged)} could not be judged because a browser is missing</> : null}.
          </li>
        )}
        {r.identified > 0 && <li>Only visitors who later gave an email or phone can be recognised at all, so the real number of lost ids is higher than this.</li>}
      </ul>
    </div>
  );
}

function Headline({ st }) {
  if (st.state === 'working') {
    return (
      <p className="text-[13.5px] leading-relaxed">
        Serving from your own {st.kind === 'path' ? 'domain' : 'subdomain'}
        {st.host ? <> (<b className="font-mono">{st.kind === 'path' ? `${st.host}/${st.token}` : st.host}</b>)</> : null}.
        {st.ready ? ' A test request went through it end to end, so new snippets load from it; it starts carrying traffic once your pages use the new code.'
          : st.quiet ? ' No tracking traffic at all in the last 24 hours, so there was nothing to check it against; it counts as working until traffic says otherwise.' : ' It carried a request in the last 24 hours.'}
      </p>
    );
  }
  if (st.state === 'stopped') {
    const s = st.stopped?.[0] || {};
    return (
      <p className="text-[13.5px] leading-relaxed">
        Your tracking {s.kind === 'path' ? 'path' : 'subdomain'}{s.host ? <> on <b className="font-mono">{s.host}</b></> : null} last
        carried a request {day(s.last_seen_at) ? `on ${day(s.last_seen_at)}` : 'a while ago'}, while tracking traffic kept arriving
        elsewhere in the last 24 hours. Pages whose tag loads through it send nothing while it is down, and new snippets load from
        Cookiebees until it works again.
      </p>
    );
  }
  return (
    <p className="text-[13.5px] leading-relaxed">
      {st.state === 'not_working'
        ? 'A tracking domain is set up but has not carried a request yet, so'
        : 'No tracking domain is set up, so'} every tag loads from Cookiebees&apos; domain: a third-party request that blockers can drop,
      with a visitor id Safari forgets after 7 days. Here is what that costs in your own data.
    </p>
  );
}

/**
 * The same verdict as a row for the Alerts bell (components/layout/alerts-bell):
 * nothing while the domain works and every page is on the new tag, otherwise
 * one line with the setup link, and the full panel (the cost in the
 * workspace's own data, or the old-tag pages) when the row is opened.
 */
export function useFirstPartyAlerts() {
  const { data: st } = useFirstPartyStatus();
  const working = st?.state === 'working';
  const oldCount = useOldTagCount(Boolean(working));
  if (!st) return [];
  if (working && !oldCount) return [];
  const tone = working ? 'notice' : st.state === 'stopped' ? 'bad' : 'warn';
  const text = working
    ? `${oldCount.toLocaleString('en-IN')} page${oldCount === 1 ? '' : 's'} on the old tag`
    : st.state === 'stopped' ? 'Tracking domain stopped' : 'Tracking is not first-party';
  return [{
    id: 'first-party',
    tone,
    text,
    to: TRACKING_DOMAIN_SETUP,
    linkLabel: working ? 'Open tracking domain' : st.state === 'stopped' ? 'Check the forwarding rule' : 'Set up your tracking domain',
    detail: () => <FirstPartyPanel st={st} working={working} />,
  }];
}

/** What the chip's popover said, reusable wherever the verdict is opened. */
export function FirstPartyPanel({ st, working, onDone }) {
  return (
    <>
      <p className="text-[14px] font-semibold">
        {working ? 'Your tracking is first-party' : st.state === 'stopped' ? 'Your tracking domain stopped' : 'Your tracking is not first-party'}
      </p>
      <div className="mt-1"><Headline st={st} /></div>
      {working ? (
        <div className="mt-3"><OldTagPages enabled max={20} /></div>
      ) : (
        <>
          <div className="mt-3"><CostTiles /></div>
          <p className="mt-3 border-t border-border pt-2.5 text-[13px] leading-relaxed text-muted-foreground">
            A tracking path on your own site has no DNS record for a blocker to match and no CNAME for Safari to call
            cloaking, and we re-issue the visitor id as a server-set cookie for 400 days instead of the 7 Safari allows a
            script. A tracking subdomain is better than our domain, but Safari still caps it at 7 days.
          </p>
        </>
      )}
      <div className="mt-3 flex items-center justify-between gap-2">
        <GuideLink href={`${KB_BASE}/tracking/tracking-domain-status/`} label="How this is decided" />
        {onDone && (
          <Button asChild size="sm" onClick={onDone}>
            <Link to={TRACKING_DOMAIN_SETUP}>{working ? 'Open tracking domain' : st.state === 'stopped' ? 'Check the forwarding rule' : 'Set up your tracking domain'}</Link>
          </Button>
        )}
      </div>
    </>
  );
}

export default FirstPartyPanel;
